import { api } from '~/trpc-extra/api';
import type { TestContentMutationContext } from '../callback-handles';
import { handleMutationError, handleMutationSuccess, cancelTestContentQueries } from '../callback-handles';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import { useSetAtom } from 'jotai';
import { selectedOpsIdsAtom } from '~/atoms/no-code-atoms';

export const useSkipOperations = ({
  testContentId,
  parentContext,
}: {
  testContentId: string;
  // parentContext: see callback-handles.ts comment
  parentContext?: OperationParentContext;
}) => {
  const trpcUtils = api.useUtils();
  const setSelectedOpsIds = useSetAtom(selectedOpsIdsAtom);

  return api.operation.updateSkipMany.useMutation<TestContentMutationContext>({
    onSuccess: () => handleMutationSuccess({ trpcUtils, testContentId, parentContext }),
    onError: (_error, _variables, context) =>
      handleMutationError({
        trpcUtils,
        testContentId,
        message: "Error unable to update actions' skip status",
        context,
        parentContext,
      }),
    onMutate: async ({ operationIds, skip }) => {
      await cancelTestContentQueries({ trpcUtils, testContentId, parentContext });
      const prevData = trpcUtils.testContent.get.getData({ id: testContentId });
      if (!prevData) {
        return { prevData: null };
      }

      const operationIdsSet = new Set(operationIds);

      setSelectedOpsIds((prev) => prev.filter((id) => !operationIdsSet.has(id)));
      const newData = {
        ...prevData,
        steps: prevData.steps.map((step) =>
          step.operations ?
            {
              ...step,
              operations: step.operations.map((op) => (operationIdsSet.has(op.id) ? { ...op, skip } : op)),
            }
          : step,
        ),
      };

      trpcUtils.testContent.get.setData({ id: testContentId }, newData);
      return { prevData };
    },
  });
};
